import { AuthLayout } from '@ifca-root/react-component/src/components/AuthLayout/AuthLayout';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import SnackBarMsg from '@ifca-root/react-component/src/components/SnackBar/SnackBarMsg';
import {
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import {
	LoggedInUserProfileDocument,
	LoggedInUserProfileQuery,
	useLoginMutation,
	useVerifyMfaLoginMutation,
	useCompletePasswordChangeMutation,
} from 'generated/graphql';
import { encryptMessage } from 'helpers/Crypto/JSEncrypt';
import React, { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import QRCode from 'qrcode.react';
import Background from '../../../src/assets/images/HR-Background.jpg';
import Logo from '../../../src/assets/images/MenuX-Logo.svg';
import { setAccessToken } from '../../AccessToken';
import { POSClient } from '../../POSClient';
import { SocketContext } from 'containers/Socket/SocketContext';
import { MFA_COPY } from './MFACopy';
import { ForcePasswordChangeDialog } from './ForcePasswordChangeDialog';

interface ILoginForm {
	email: string;
	password: string;
	showPassword: boolean;
	rememberMe: boolean;
	product: string;
}

interface MfaChallengeState {
	mfaToken?: string | null;
	mfaEnrolled: boolean;
	otpURL?: string | null;
}

const mfaTitleStyle: React.CSSProperties = {
	color: '#ffffff',
	fontSize: '20px',
	fontWeight: 600,
	textAlign: 'center',
	lineHeight: 1.3,
	padding: '4px 0px 12px 0px',
};

const mfaBodyStyle: React.CSSProperties = {
	color: 'rgba(255, 255, 255, 0.85)',
	fontSize: '12px',
	lineHeight: 1.5,
	whiteSpace: 'normal',
	overflow: 'visible',
	textOverflow: 'clip',
	wordBreak: 'break-word',
	paddingBottom: '8px',
};

export const Login = () => {
	let history = useHistory();
	let location = useLocation();
	const { socket }: any = useContext(SocketContext);

	const rememberme: any = localStorage.getItem('rememberme');
	const appCode: any = localStorage.getItem('appCode');

	const [outletID, setOutletID] = useState(null);

	localStorage.clear();

	if (rememberme !== null) {
		localStorage.setItem('rememberme', rememberme);
	}

	const [rememberMe, setRememberMe] = useState(
		rememberme !== null ? true : false,
	);
	const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeState | null>(
		null,
	);
	const [otpCode, setOtpCode] = useState('');
	const [passwordChangeToken, setPasswordChangeToken] = useState<string | null>(
		null,
	);
	const [passwordChangeReason, setPasswordChangeReason] = useState<string | null>(
		null,
	);
	const [values, setValues] = useState(false);
	const [open, setOpen] = useState(false);
	const [openAlert, setOpenAlert] = useState(false);
	const [message, setMessage] = useState('');

	const handleCloseAlert = () => {
		setOpenAlert(false);
	};

	const handleCloseSnackBar = () => {
		setOpen(false);
		history.push('/profile');
	};
	const MESSAGE = 'Password Changed Successfully';

	const handleClickShowPassword = () => {
		setValues(!values);
	};

	const handleMouseDownPassword = (event: any) => {
		event.preventDefault();
	};
	const handleCheckRememberMe = (checked: boolean) => {
		setRememberMe(checked);
	};

	// useForm set DefaultValues, validationSchema
	const { handleSubmit, errors, control, getValues, reset } = useForm<
		ILoginForm
	>({
		defaultValues: {
			email: rememberme !== null ? JSON.parse(rememberme)?.username : '',
			password: rememberme !== null ? JSON.parse(rememberme)?.password : '',
		},
	});

	const handleLoginSuccess = (loginResult: any) => {
		if (loginResult?.accessToken) {
			setAccessToken(loginResult.accessToken);
			sessionStorage.setItem('key', loginResult.accessToken);
		}

		if (rememberMe) {
			localStorage.setItem(
				'rememberme',
				JSON.stringify({
					username: getValues('email'),
					password: getValues('password'),
				}),
			);
		} else {
			localStorage.removeItem('rememberme');
		}

		setTimeout(() => {
			history.push({ pathname: `/redirect`, state: loginResult?.user });
		}, 100);

		if (loginResult?.user) {
			socket.emit('data', {
				Status: 'LOGIN',
				OutletID: loginResult.user.lastestAccessedEntity,
				UserID: loginResult.user.ID,
			});
		}
	};

	const applyLoginResult = (loginResult: any) => {
		if (loginResult?.mustChangePassword && loginResult?.passwordChangeToken) {
			sessionStorage.removeItem('key');
			setAccessToken('');
			setPasswordChangeReason(loginResult.passwordChangeReason || 'FIRST');
			setPasswordChangeToken(loginResult.passwordChangeToken);
			return;
		}
		if (loginResult?.mfaRequired) {
			sessionStorage.removeItem('key');
			setAccessToken('');
			setOtpCode('');
			setMfaChallenge({
				mfaToken: loginResult.mfaToken,
				mfaEnrolled: !!loginResult.mfaEnrolled,
				otpURL: loginResult.otpURL,
			});
			return;
		}
		setPasswordChangeToken(null);
		setMfaChallenge(null);
		handleLoginSuccess(loginResult);
	};

	const [login, { loading }] = useLoginMutation({
		onError: error => {
			error?.graphQLErrors?.map(({ message }) => {
				setMessage(message);
				setOpenAlert(true);
			});
		},
		update: (store, { data }) => {
			if (!data?.login?.user) {
				return null;
			}
			store.writeQuery<LoggedInUserProfileQuery>({
				query: LoggedInUserProfileDocument,
				data: {
					__typename: 'Query',
					loggedInUserProfile: data.login.user as any,
				},
			});
		},
		onCompleted: data => {
			applyLoginResult(data?.login);
		},
		client: POSClient as any,
	});

	const [completePasswordChange, { loading: changingPassword }] =
		useCompletePasswordChangeMutation({
			onError: error => {
				const gqlMessage = error?.graphQLErrors?.[0]?.message;
				setMessage(gqlMessage || error?.message || 'Unable to change password');
				setOpenAlert(true);
			},
			onCompleted: ({ completePasswordChange }) => {
				setPasswordChangeToken(null);
				applyLoginResult(completePasswordChange);
			},
			client: POSClient as any,
		});

	const [verifyMfaLogin, { loading: verifyMfaLoading }] =
		useVerifyMfaLoginMutation({
			onError: error => {
				const gqlMessage = error?.graphQLErrors?.[0]?.message;
				setMessage(
					gqlMessage || error?.message || 'Invalid OTP code, please try again',
				);
				setOpenAlert(true);
				setOtpCode('');
			},
			update: (store, { data }) => {
				if (!data?.verifyMfaLogin?.user) {
					return null;
				}
				store.writeQuery<LoggedInUserProfileQuery>({
					query: LoggedInUserProfileDocument,
					data: {
						__typename: 'Query',
						loggedInUserProfile: data.verifyMfaLogin.user as any,
					},
				});
			},
			onCompleted: ({ verifyMfaLogin }) => {
				setOtpCode('');
				setMfaChallenge(null);
				applyLoginResult(verifyMfaLogin);
			},
			client: POSClient as any,
		});

	const onSubmit = (data: any) => {
		login({
			variables: {
				loginID: data?.email,
				password: encryptMessage(data?.password),
			},
		});
	};

	const onVerifyOtp = () => {
		if (!mfaChallenge?.mfaToken || !otpCode) return;
		verifyMfaLogin({
			variables: {
				mfaToken: mfaChallenge.mfaToken,
				otpCode,
			},
		});
	};

	const handleLoginAnotherAccount = () => {
		setMfaChallenge(null);
		setOtpCode('');
		setAccessToken('');
		sessionStorage.removeItem('key');
		reset();
	};

	return (
		<>
			{(loading || verifyMfaLoading || changingPassword) && <Loading />}
			<AuthLayout logo={Logo} image={Background} className="margin-override">
				<span className="page-title">Login</span>
				{mfaChallenge ? (
					<form
						onSubmit={e => {
							e.preventDefault();
							onVerifyOtp();
						}}
						id="submit-form"
					>
						<div className="form-box">
							<div style={mfaTitleStyle}>
								{mfaChallenge.mfaEnrolled
									? MFA_COPY.verifyTitle
									: MFA_COPY.enrolTitle}
							</div>
							{mfaChallenge.mfaEnrolled ? (
								<div style={mfaBodyStyle}>{MFA_COPY.verifyBody}</div>
							) : (
								<>
									<div style={mfaBodyStyle}>{MFA_COPY.enrolBody1}</div>
									<div style={mfaBodyStyle}>{MFA_COPY.enrolBody2}</div>
									{mfaChallenge.otpURL && (
										<Grid container justify="center">
											<div
												style={{
													background: '#ffffff',
													padding: '12px',
													margin: '4px 0px 12px 0px',
												}}
											>
												<QRCode value={mfaChallenge.otpURL} size={160} />
											</div>
										</Grid>
									)}
								</>
							)}
							<TextField
								fullWidth
								autoComplete="off"
								autoFocus
								name="otpCode"
								label={MFA_COPY.otpLabel}
								value={otpCode}
								onChange={e => setOtpCode(e.target.value)}
								inputProps={{ inputMode: 'numeric', maxLength: 6 }}
							/>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								className="login-btn"
								disabled={!otpCode || verifyMfaLoading}
							>
								{MFA_COPY.verifyButton}
							</Button>
							<div
								style={{
									width: '100%',
									display: 'flex',
									justifyContent: 'center',
									paddingTop: '10px',
								}}
							>
								<span className="forget-link" onClick={handleLoginAnotherAccount}>
									{MFA_COPY.anotherAccount}
								</span>
							</div>
						</div>
					</form>
				) : (
				<form onSubmit={handleSubmit(onSubmit)} id="submit-form">
					<div className="form-box">
						<Controller
							as={TextField}
							fullWidth
							helperText={errors?.email?.message}
							error={errors?.email ? true : false}
							autoComplete="off"
							name="email"
							label="Email/Username"
							control={control}
							data-qa="usernameInput"
						/>
						<Grid container>
							<Controller
								as={TextField}
								fullWidth
								helperText={errors?.password?.message}
								error={errors?.password ? true : false}
								autoComplete="off"
								name="password"
								label="Password "
								type={values ? 'text' : 'password'}
								value={getValues('password')}
								//   onChange={handleChange('password')}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="Password"
												onClick={handleClickShowPassword}
												onMouseDown={handleMouseDownPassword}
											>
												{values ? <Visibility /> : <VisibilityOff />}
											</IconButton>
										</InputAdornment>
									),
								}}
								control={control}
								data-qa="passwordInput"
							/>
						</Grid>

						<div className="other-auth">
							<FormControlLabel
								control={
									<Checkbox
										name="checkedB"
										color="primary"
										checked={rememberMe}
										onChange={e => handleCheckRememberMe(e.target.checked)}
									/>
								}
								label="Remember Me"
							/>
							<span
								className="forget-link"
								onClick={() => history.push('/forgot-password')}
								data-qa="forgotPasswordLink"
							>
								Forgot Password?
							</span>
						</div>

						<Button
							type="submit"
							variant="contained"
							color="primary"
							className="login-btn"
							// onClick={() => history.push('/')}
							data-qa="loginButton"
							// onClick={() => {
							// 	// history.push(loginRedirectPath);
							// }}
						>
							Login
						</Button>
					</div>
				</form>
				)}

				<ForcePasswordChangeDialog
					open={!!passwordChangeToken}
					reason={passwordChangeReason}
					askCurrent={false}
					loading={changingPassword}
					onLogout={() => {
						setPasswordChangeToken(null);
						setPasswordChangeReason(null);
					}}
					onSubmit={({ newPassword }) => {
						if (!passwordChangeToken) return;
						completePasswordChange({
							variables: {
								passwordChangeToken,
								newPassword: encryptMessage(newPassword),
							},
						});
					}}
				/>
				<CommonDialog
					fullWidth={true}
					open={openAlert}
					onClose={handleCloseAlert}
					sections={{
						header: {
							dynamic: (
								<div className="">
									<div className="dialog-dynamic-content">
										<div className="session">
											<div className="title">Login Error</div>
										</div>
									</div>
								</div>
							),
						},
						body: () => <div>{message}</div>,
						footer: {
							actions: [
								{
									displayText: 'Ok',
									props: {
										onClick: () => handleCloseAlert(),
										variant: 'contained',
										color: 'primary',
									},
								},
							],
						},
					}}
				/>
				<SnackBarMsg
					open={open}
					message={MESSAGE}
					onClose={handleCloseSnackBar}
				/>
			</AuthLayout>
		</>
	);
};
